import {parse, parseISO, isSameDay, isAfter, isToday, startOfDay, addYears, isEqual} from 'date-fns';
import {isNotStringOrEmptyString} from "@utils/string";
import { format, isValid } from 'date-fns';

export function isDateGreaterThenToday(dateString) {
    const date = parseISO(dateString);
    const today = startOfDay(new Date());

    return isAfter(date, today) && !isSameDay(date, today);
}

export function isDateGreaterOrEqualToday(dateString) {
    const date = parseISO(dateString);
    const today = startOfDay(new Date());

    return isAfter(date, today) || isSameDay(date, today) || isToday(date);
}

export function getTomorrowsDate() {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
}


/**
 * Checks if a person is at least N years old.
 *
 * @param {string} birthDateString - The person's date of birth (e.g., '1990-05-15').
 * @param {string} dateFormat - The format of the birthDateString (e.g., 'yyyy-MM-dd').
 * @param {number} n - The target age in years (must be a non-negative integer).
 * @returns {boolean} True if the person is N years old or older, otherwise false.
 */
export function isAtLeastNYearsOld(birthDateString, dateFormat = 'yyyy-MM-dd', n = 18) {
   // Ensure n is a valid number.
   if (typeof n !== 'number' || n < 0 || !Number.isInteger(n)) {
      console.error('The target age (n) must be a non-negative integer.');
      return false;
   }
   
   if(isNotStringOrEmptyString(dateFormat)) {
      dateFormat = 'yyyy-MM-dd';
   }
   
   // Parse the birth date string using the provided format.
   const birthDate = parse(birthDateString, dateFormat, new Date());
   
   // Check if the parsed date is valid.
   if (isNaN(birthDate.getTime())) {
      console.error(`Invalid date string or format provided. ${birthDateString} (${dateFormat})`);
      return false;
   }
   
   // Calculate the date of the person's Nth birthday.
   const nthBirthday = addYears(birthDate, n);
   
   // Get the current date for comparison.
   const today = new Date();
   
   // Return true if today is after or on the nth birthday.
   return isAfter(today, nthBirthday) || isEqual(today, nthBirthday);
}

export function isAtLeast18YearsOld(birthDateString, dateFormat) {

   return isAtLeastNYearsOld(birthDateString, dateFormat, 18);
   
}


/**
 * Converts a datetime string into a specified format using date-fns standards.
 *
 * @param {string | null | undefined} datetimeString - The date/time string to format (e.g., ISO 8601).
 * @param {string} [formatString='dd-MM-yyyy'] - The date-fns format string (e.g., 'dd/MM/yyyy HH:mm').
 * @param {string} [fallbackValue='-'] - The value to return if the input is null, undefined, or invalid.
 * @returns {string} The formatted date string or the fallback value.
 */
export const formatDatetime = (
   datetimeString,
   formatString = 'dd-MM-yyyy',
   fallbackValue = '-'
) => {
   // 1. Handle Null/Empty/Invalid Input Type
   if (!datetimeString || typeof datetimeString !== 'string' || datetimeString.trim() === '') {
      return fallbackValue;
   }
   
   try {
      // Attempt to create a Date object
      const date = new Date(datetimeString);
      
      // 2. Check if the Date object is valid
      if (!isValid(date)) {
         return fallbackValue;
      }
      
      // 3. Format the Date using the specified format string
      // date-fns uses 'dd' for day, 'MM' for month (with leading zero), and 'yyyy' for year.
      return format(date, formatString);
      
   } catch (error) {
      // 4. Handle unexpected errors
      //console.error('Error formatting datetime:', error);
      return fallbackValue;
   }
};